'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Plus, RefreshCw, Trash2 } from 'lucide-react';
import Badge from '@/components/ui/Badge';

interface Domain {
  id: string;
  domain: string;
  status: 'verified' | 'pending' | 'failed';
  spf: boolean;
  dkim: boolean;
  dmarc: boolean;
  addedAt: string;
}

const mockDomains: Domain[] = [
  { id: 'dom-001', domain: 'mailflow.io', status: 'verified', spf: true, dkim: true, dmarc: true, addedAt: 'Mar 1, 2026' },
  { id: 'dom-002', domain: 'campaigns.brandstudio.com', status: 'verified', spf: true, dkim: true, dmarc: false, addedAt: 'May 12, 2026' },
  { id: 'dom-003', domain: 'news.techventures.in', status: 'pending', spf: true, dkim: false, dmarc: false, addedAt: 'Jul 28, 2026' },
  { id: 'dom-004', domain: 'promo.agencia.br', status: 'failed', spf: false, dkim: false, dmarc: false, addedAt: 'Jul 30, 2026' },
];

const statusVariant: Record<Domain['status'], 'success' | 'warning' | 'danger'> = {
  verified: 'success',
  pending: 'warning',
  failed: 'danger',
};

const DnsCheck = ({ ok }: { ok: boolean }) =>
  ok
    ? <CheckCircle2 size={14} className="text-green-600" />
    : <XCircle size={14} className="text-red-500" />;

export default function SenderDomainsSettings() {
  const [domains, setDomains] = useState<Domain[]>(mockDomains);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newDomain) return;
    // Backend integration point: POST /api/sender-domains
    setAdding(true);
    await new Promise((r) => setTimeout(r, 800));
    const d: Domain = {
      id: `dom-${Date.now()}`,
      domain: newDomain,
      status: 'pending',
      spf: false, dkim: false, dmarc: false,
      addedAt: 'Jul 31, 2026',
    };
    setDomains((prev) => [...prev, d]);
    setNewDomain('');
    setAdding(false);
    toast.success('Domain added — DNS verification started');
  };

  const handleDelete = (id: string) => {
    setDomains((prev) => prev.filter((d) => d.id !== id));
    toast.success('Domain removed');
  };

  return (
    <div className="space-y-6">
      <div className="card p-6 space-y-5">
        <div>
          <h2 className="section-header">Sender Domains</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Verify your sending domains to improve deliverability and avoid spam filters.
          </p>
        </div>

        {/* Add domain */}
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="e.g. mail.yourdomain.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            className="input-base flex-1"
          />
          <button onClick={handleAdd} disabled={adding || !newDomain} className="btn-primary">
            {adding ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
            ) : <Plus size={15} />}
            Add Domain
          </button>
        </div>

        {/* Domains table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-3 text-left metric-label">Domain</th>
                <th className="pb-3 text-left metric-label">Status</th>
                <th className="pb-3 text-center metric-label">SPF</th>
                <th className="pb-3 text-center metric-label">DKIM</th>
                <th className="pb-3 text-center metric-label">DMARC</th>
                <th className="pb-3 text-left metric-label">Added</th>
                <th className="pb-3 text-left metric-label w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="py-3 pr-4 text-sm font-medium text-foreground">{d.domain}</td>
                  <td className="py-3 pr-4">
                    <Badge variant={statusVariant[d.status]}>
                      {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="py-3 pr-4 text-center"><DnsCheck ok={d.spf} /></td>
                  <td className="py-3 pr-4 text-center"><DnsCheck ok={d.dkim} /></td>
                  <td className="py-3 pr-4 text-center"><DnsCheck ok={d.dmarc} /></td>
                  <td className="py-3 pr-4 text-sm text-muted-foreground">{d.addedAt}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toast.info('DNS re-check triggered')}
                        title="Re-check DNS records"
                        className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all"
                      >
                        <RefreshCw size={13} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDelete(d.id)}
                        title="Remove domain"
                        className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-50 transition-all"
                      >
                        <Trash2 size={13} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DNS help card */}
      <div className="card p-5 border-amber-200 bg-amber-50/50">
        <p className="text-sm font-semibold text-amber-800 mb-1">DNS Setup Instructions</p>
        <p className="text-xs text-amber-700 leading-relaxed">
          Add the following DNS records to your domain registrar to complete verification.
          SPF, DKIM, and DMARC records are all required for full deliverability protection.
          Changes may take up to 48 hours to propagate.
        </p>
      </div>
    </div>
  );
}